import axios, { AxiosInstance } from 'axios';
import { 
  AdminUser, 
  AdminLoginCredentials, 
  AdminApiResponse, 
  AdminDashboardStats,
  UserManagementData,
  UserFilterOptions,
  UserFormData,
  AdminSettings,
  UserActivityLog,
  SystemHealth,
  RevenueAnalytics,
  ChartData
} from '../types';
import { getCurrentConfig } from '../../config/environments';

// API Configuration - Use same environment as main application
const config = getCurrentConfig();
const API_BASE_URL = config.apiBaseUrl;
const ADMIN_ENDPOINTS = {
  AUTH: '/admin_auth.php',
  DASHBOARD: '/admin_dashboard.php',
  USERS: '/admin_users.php',
  SETTINGS: '/admin_settings.php',
  ANALYTICS: '/admin_analytics.php',
  SYSTEM: '/admin_system.php',
  WALLET_SUBS: '/admin_wallet_subs.php',
  SUBSCRIPTION_UPDATE: '/admin_subscription_update.php',
};

// HTTP client for admin API (using axios for web compatibility)
class AdminHttpClient {
  private api: AxiosInstance;

  constructor(baseURL: string) {
    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });

    // Add request interceptor for auth token
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('admin_auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('Admin API Error:', error);
        // Handle authentication errors
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_auth_token');
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, headers?: Record<string, string>): Promise<T> {
    const response = await this.api.get(url, { headers });
    return response.data;
  }

  async post<T>(url: string, data?: any, headers?: Record<string, string>): Promise<T> {
    const response = await this.api.post(url, data, { headers });
    return response.data;
  }

  async put<T>(url: string, data?: any, headers?: Record<string, string>): Promise<T> {
    const response = await this.api.put(url, data, { headers });
    return response.data;
  }

  async delete<T>(url: string, data?: any, headers?: Record<string, string>): Promise<T> {
    const response = await this.api.delete(url, { data, headers });
    return response.data;
  }
}

// Create admin HTTP client instance
const adminApi = new AdminHttpClient(API_BASE_URL);

// Admin Authentication API
export const adminAuthApi = {
  login: (credentials: AdminLoginCredentials): Promise<AdminApiResponse<{ user: AdminUser; token: string }>> => 
    adminApi.post(ADMIN_ENDPOINTS.AUTH, credentials),
  
  verifyToken: (): Promise<AdminApiResponse<{ user: AdminUser }>> => 
    adminApi.get(ADMIN_ENDPOINTS.AUTH),
  
  changePassword: (passwords: { current_password: string; new_password: string }): Promise<AdminApiResponse<null>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.AUTH}?action=change-password`, passwords),
};

// Admin Dashboard API
export const adminDashboardApi = {
  getStats: (): Promise<AdminApiResponse<AdminDashboardStats>> => 
    adminApi.get(ADMIN_ENDPOINTS.DASHBOARD),
  
  getRevenueAnalytics: (period: string = '30d'): Promise<AdminApiResponse<RevenueAnalytics>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.ANALYTICS}?action=revenue&period=${period}`),
  
  getSystemHealth: (): Promise<AdminApiResponse<SystemHealth>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.SYSTEM}?action=health`),
};

// User Management API
export const adminUsersApi = {
  getUsers: (params: UserFilterOptions = {}): Promise<AdminApiResponse<UserManagementData>> => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.role) queryParams.append('role', params.role);
    if (params.status) queryParams.append('status', params.status);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    
    const url = queryParams.toString() ? `${ADMIN_ENDPOINTS.USERS}?${queryParams}` : ADMIN_ENDPOINTS.USERS;
    return adminApi.get(url);
  },
  
  getUserById: (id: number): Promise<AdminApiResponse<AdminUser>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.USERS}/${id}`),
  
  createUser: (userData: UserFormData): Promise<AdminApiResponse<AdminUser>> => 
    adminApi.post(ADMIN_ENDPOINTS.USERS, userData),
  
  updateUser: (id: number, userData: Partial<UserFormData>): Promise<AdminApiResponse<AdminUser>> => 
    adminApi.put(`${ADMIN_ENDPOINTS.USERS}?id=${id}`, userData),
  
  deleteUser: (id: number): Promise<AdminApiResponse<null>> => 
    adminApi.delete(ADMIN_ENDPOINTS.USERS, { id }),
  
  restoreUser: (id: number): Promise<AdminApiResponse<null>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.USERS}?action=restore`, { id }),
  
  resetPassword: (id: number, new_password: string): Promise<AdminApiResponse<null>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.USERS}?action=reset_password`, { id, new_password }),
  
  toggleUserStatus: (id: number, makeActive: boolean): Promise<AdminApiResponse<null>> => 
    adminApi.put(`${ADMIN_ENDPOINTS.USERS}?id=${id}`, { is_active: makeActive ? 1 : 0 }),
  
  getUserActivity: (id: number, limit: number = 10): Promise<AdminApiResponse<UserActivityLog[]>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.USERS}/${id}/activity?limit=${limit}`),
};

// Admin Settings API
export const adminSettingsApi = {
  getSettings: (): Promise<AdminApiResponse<AdminSettings>> => 
    adminApi.get(ADMIN_ENDPOINTS.SETTINGS),
  
  updateSettings: (settings: Partial<AdminSettings>): Promise<AdminApiResponse<AdminSettings>> => 
    adminApi.put(ADMIN_ENDPOINTS.SETTINGS, settings),
  
  updateSystemPassword: (passwords: { current_password: string; new_password: string }): Promise<AdminApiResponse<null>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.SETTINGS}?action=system_password`, passwords),
  
  updateMysqlPassword: (passwords: { current_password: string; new_password: string }): Promise<AdminApiResponse<null>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.SETTINGS}?action=mysql_password`, passwords),
  
  testDatabaseConnection: (): Promise<AdminApiResponse<{ connected: boolean; message: string }>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.SETTINGS}?action=test_db`),
  
  backupDatabase: (): Promise<AdminApiResponse<{ backup_file: string; message: string }>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.SETTINGS}?action=backup`),
  
  restoreDatabase: (backup_file: string): Promise<AdminApiResponse<{ message: string }>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.SETTINGS}?action=restore`, { backup_file }),
  
  getDeletedUsers: (): Promise<AdminApiResponse<{ users: AdminUser[] }>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.USERS}?action=deleted`),
  
  restoreUser: (id: number): Promise<AdminApiResponse<null>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.USERS}/${id}/restore`),
};

// Admin Analytics API
export const adminAnalyticsApi = {
  getRevenueAnalytics: (period: string = '30d'): Promise<AdminApiResponse<RevenueAnalytics>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.ANALYTICS}?action=revenue&period=${period}`),
  
  getUserAnalytics: (period: string = '30d'): Promise<AdminApiResponse<ChartData>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.ANALYTICS}?action=users&period=${period}`),
  
  getUserGrowth: (period: string = '30d'): Promise<AdminApiResponse<ChartData>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.ANALYTICS}?action=user_growth&period=${period}`),
  
  getOrderAnalytics: (period: string = '30d'): Promise<AdminApiResponse<ChartData>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.ANALYTICS}?action=orders&period=${period}`),
  
  getSubscriptionAnalytics: (period: string = '30d'): Promise<AdminApiResponse<ChartData>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.ANALYTICS}?action=subscriptions&period=${period}`),
  
  getSystemMetrics: (): Promise<AdminApiResponse<SystemHealth>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.SYSTEM}?action=metrics`),
};

// Wallet and Subscription Management API
export const adminWalletSubsApi = {
  getWalletStats: (): Promise<AdminApiResponse<{ total_balance: number; total_transactions: number; active_users: number }>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.WALLET_SUBS}?action=wallet_stats`),
  
  getSubscriptionStats: (): Promise<AdminApiResponse<{ total_subscriptions: number; active_subscriptions: number; expired_subscriptions: number }>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.WALLET_SUBS}?action=subscription_stats`),
  
  getWalletTransactions: (params: { page?: number; limit?: number; user_id?: number; type?: string } = {}): Promise<AdminApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });
    const url = queryParams.toString() ? `${ADMIN_ENDPOINTS.WALLET_SUBS}?action=wallet_transactions&${queryParams}` : `${ADMIN_ENDPOINTS.WALLET_SUBS}?action=wallet_transactions`;
    return adminApi.get(url);
  },
  
  getSubscriptions: (params: { page?: number; limit?: number; status?: string; user_id?: number; search?: string } = {}): Promise<AdminApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });
    const url = queryParams.toString() ? `${ADMIN_ENDPOINTS.WALLET_SUBS}?action=subscriptions&${queryParams}` : `${ADMIN_ENDPOINTS.WALLET_SUBS}?action=subscriptions`;
    return adminApi.get(url);
  },
  
  updateSubscription: (subscriptionData: any): Promise<AdminApiResponse<any>> => 
    adminApi.post(ADMIN_ENDPOINTS.SUBSCRIPTION_UPDATE, subscriptionData),
  
  // cancelSubscription: (subscriptionId: number): Promise<AdminApiResponse<null>> => 
  //   adminApi.post(`${ADMIN_ENDPOINTS.WALLET_SUBS}?action=cancel_subscription`, { subscription_id: subscriptionId }),
  
  getAllUsersWithWallet: (params: { page?: number; search?: string; status?: string } = {}): Promise<AdminApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });
    const url = queryParams.toString() ? `${ADMIN_ENDPOINTS.WALLET_SUBS}/users?${queryParams}` : `${ADMIN_ENDPOINTS.WALLET_SUBS}/users`;
    return adminApi.get(url);
  },
  
  topupWallet: (data: { user_id: number; amount: number; description: string }): Promise<AdminApiResponse<any>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.WALLET_SUBS}/topup`, data),
};

// System Administration API
export const adminSystemApi = {
  getSystemLogs: (params: { page?: number; limit?: number; level?: string; date_from?: string; date_to?: string } = {}): Promise<AdminApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });
    const url = queryParams.toString() ? `${ADMIN_ENDPOINTS.SYSTEM}?action=logs&${queryParams}` : `${ADMIN_ENDPOINTS.SYSTEM}?action=logs`;
    return adminApi.get(url);
  },
  
  getSystemHealth: (): Promise<AdminApiResponse<SystemHealth>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.SYSTEM}?action=health`),
  
  clearLogs: (older_than_days: number = 30): Promise<AdminApiResponse<{ message: string; deleted_count: number }>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.SYSTEM}?action=clear_logs`, { older_than_days }),
  
  getSystemInfo: (): Promise<AdminApiResponse<{ php_version: string; mysql_version: string; server_info: string; disk_usage: any; memory_usage: any; cpu_usage: any; last_backup: string }>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.SYSTEM}?action=info`),
  
  getBackups: (): Promise<AdminApiResponse<{ backups: any[] }>> => 
    adminApi.get(`${ADMIN_ENDPOINTS.SYSTEM}?action=backups`),
  
  createBackup: (): Promise<AdminApiResponse<{ message: string; backup_file: string }>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.SYSTEM}?action=create_backup`),
  
  restoreBackup: (backup_file: string): Promise<AdminApiResponse<{ message: string }>> => 
    adminApi.post(`${ADMIN_ENDPOINTS.SYSTEM}?action=restore_backup`, { backup_file }),
  
  deleteBackup: (backup_file: string): Promise<AdminApiResponse<{ message: string }>> => 
    adminApi.delete(`${ADMIN_ENDPOINTS.SYSTEM}?action=delete_backup&backup_file=${backup_file}`),
};

export default adminApi;